import { Injectable, computed, inject } from '@angular/core';
import { environment } from '../../../environments/environment.local';
import { ModeService } from '../mode/mode.service';
import { DemoStore } from './store';
import { DEMO_OWNER_ID, DEMO_TENANT_ID_CONST } from './seed';
import {
  ResourceAuthorization,
  ResourceAuthTarget,
  TenantAuthService,
} from '../auth/tenant-auth.service';

const DEMO_SUBDOMAIN = 'demo';
const DEMO_TOKEN = 'demo-token';

@Injectable({ providedIn: 'root' })
export class DemoModeService {
  private readonly mode = inject(ModeService);
  private readonly store = inject(DemoStore);
  private readonly auth = inject(TenantAuthService);

  readonly isDemoActive = computed(() => this.mode.subdomain() === DEMO_SUBDOMAIN);

  bootstrap(): void {
    if (!this.isDemoActive()) return;
    this.store.seedIfEmpty();
    this.ensureSession();
  }

  reset(): void {
    this.store.reset();
  }

  exit(): void {
    this.auth.clearAll();
    this.store.clear();
    if (typeof window === 'undefined') return;
    window.location.assign(`https://${environment.rootDomain}`);
  }

  private ensureSession(): void {
    if (this.auth.isAuthenticated()) return;
    const ownerEmail =
      this.store.snapshot().members.find((m) => m.id === DEMO_OWNER_ID)?.email ??
      'demo@sportdesk.ro';
    const expiresAt = new Date(Date.now() + 365 * 24 * 3600 * 1000).toISOString();
    const fullAuthorizations: readonly ResourceAuthorization[] = ALL_TARGETS.map((target) => ({
      target,
      levels: ['Read', 'Write', 'Delete'] as const,
    }));
    this.auth.acceptSession({
      tenantId: DEMO_TENANT_ID_CONST,
      subdomain: DEMO_SUBDOMAIN,
      tenantAccountId: DEMO_OWNER_ID,
      email: ownerEmail,
      role: 'Owner',
      token: DEMO_TOKEN,
      expiresAt,
      authorizations: fullAuthorizations,
    });
  }
}

const ALL_TARGETS: readonly ResourceAuthTarget[] = [
  'Tenant',
  'TenantSettings',
  'UserManagement',
  'CoachManagement',
  'Locations',
  'Groups',
  'Enrollments',
  'Classes',
  'Attendance',
  'Events',
  'TenantPayments',
  'StudentManagement',
  'Xp',
  'PublicSite',
];
